import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { getTrip } from '../lib/api';
import { getTripAccess } from '../lib/tripAccess';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, Plus, Minus, CreditCard, Spinner } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '../lib/api';

export default function ExtraCapacityCheckout() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const access = getTripAccess(code);
  const role = access?.role || (access?.legacy ? 'organiser' : null);

  const kind = searchParams.get('kind') || 'both';
  const initDP = parseInt(searchParams.get('delta_players') || '1') || 1;
  const initDR = parseInt(searchParams.get('delta_rounds') || '0') || 0;

  const [trip, setTrip] = useState(null);
  const [deltaPlayers, setDeltaPlayers] = useState(kind === 'rounds' ? 0 : initDP);
  const [deltaRounds, setDeltaRounds] = useState(kind === 'players' ? 0 : initDR);
  const [pricing, setPricing] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (role !== 'organiser') { navigate(`/trip/${code}`); return; }
    getTrip(code).then(r => setTrip(r.data)).catch(() => navigate(`/trip/${code}`));
  }, [code, role, navigate]);

  const fetchPricing = useCallback(async (dp, dr) => {
    if (dp <= 0 && dr <= 0) { setPricing(null); return; }
    setPricingLoading(true);
    try {
      const { data } = await api.get(`/organiser/trips/${code}/topup/preview?delta_players=${dp}&delta_rounds=${dr}`);
      setPricing(data);
    } catch { setPricing(null); }
    finally { setPricingLoading(false); }
  }, [code]);

  useEffect(() => {
    if (trip) fetchPricing(deltaPlayers, deltaRounds);
  }, [deltaPlayers, deltaRounds, trip, fetchPricing]);

  const handleCheckout = async () => {
    if (!pricing || pricing.total_pence <= 0) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/organiser/trips/${code}/topup-order`, {
        delta_players: deltaPlayers,
        delta_rounds: deltaRounds,
      });
      if (data.checkout_url) {
        // Redirect to Revolut checkout
        window.location.href = data.checkout_url;
      } else {
        toast.error('Could not create checkout session');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to create order');
    } finally { setSubmitting(false); }
  };

  if (!trip) return <div className="min-h-screen bg-[#F4F6F5] flex items-center justify-center"><div className="w-8 h-8 border-3 border-[#1B4332] border-t-transparent rounded-full animate-spin" /></div>;

  const maxR = trip.max_rounds || 0;
  const maxP = trip.max_players || 0;

  const Stepper = ({ value, onChange, min = 0, label, testId }) => (
    <div className="flex items-center justify-between" data-testid={testId}>
      <span className="text-sm font-semibold text-[#1D242B]">{label}</span>
      <div className="flex items-center gap-3">
        <button onClick={() => onChange(Math.max(min, value - 1))} disabled={value <= min}
          className="w-9 h-9 rounded-full border border-[#E1E6E4] flex items-center justify-center disabled:opacity-30" data-testid={`${testId}-dec`}>
          <Minus className="w-4 h-4" weight="bold" />
        </button>
        <span className="text-lg font-bold text-[#1D242B] w-8 text-center tabular-nums" data-testid={`${testId}-value`}>{value}</span>
        <button onClick={() => onChange(value + 1)}
          className="w-9 h-9 rounded-full border border-[#E1E6E4] flex items-center justify-center hover:border-[#1B4332]" data-testid={`${testId}-inc`}>
          <Plus className="w-4 h-4" weight="bold" />
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F4F6F5]" data-testid="extra-capacity-checkout">
      <div className="p-4 flex items-center gap-3 max-w-lg mx-auto">
        <button onClick={() => navigate(`/trip/${code}`)} className="w-10 h-10 rounded-full bg-white border border-[#E1E6E4] flex items-center justify-center" data-testid="back-btn">
          <ArrowLeft className="w-5 h-5 text-[#1D242B]" weight="bold" />
        </button>
        <h1 className="text-xl font-bold text-[#1D242B]">Add Extra Capacity</h1>
      </div>

      <div className="px-4 max-w-lg mx-auto space-y-4 pb-8">
        {/* Current allowance */}
        <Card className="p-4 bg-white border-[#E1E6E4] rounded-xl">
          <p className="text-xs font-semibold text-[#455561] mb-2">Current Allowance</p>
          <div className="grid grid-cols-2 gap-3 text-center">
            <div className="bg-[#F4F6F5] rounded-lg p-2.5">
              <p className="text-xl font-bold text-[#1D242B]">{maxP}</p>
              <p className="text-[10px] text-[#455561]">Players</p>
            </div>
            <div className="bg-[#F4F6F5] rounded-lg p-2.5">
              <p className="text-xl font-bold text-[#1D242B]">{maxR}</p>
              <p className="text-[10px] text-[#455561]">Rounds</p>
            </div>
          </div>
        </Card>

        {/* Steppers */}
        <Card className="p-4 bg-white border-[#E1E6E4] rounded-xl space-y-4">
          <p className="text-xs font-semibold text-[#455561]">Extra Capacity</p>
          {kind !== 'rounds' && (
            <Stepper value={deltaPlayers} onChange={setDeltaPlayers} min={0}
              label="Extra Players" testId="stepper-players" />
          )}
          {kind !== 'players' && (
            <Stepper value={deltaRounds} onChange={setDeltaRounds} min={0}
              label="Extra Rounds" testId="stepper-rounds" />
          )}
        </Card>

        {/* Pricing */}
        <Card className="p-5 bg-[#1B4332] rounded-xl text-center" data-testid="pricing-card">
          {pricingLoading ? (
            <Spinner className="w-6 h-6 text-white mx-auto animate-spin" />
          ) : pricing ? (
            <>
              {pricing.line_items?.map((item, i) => (
                <p key={i} className="text-xs text-[#E8E5DB]/80 mb-1">{item.label}: £{(item.amount_pence / 100).toFixed(2)}</p>
              ))}
              <p className="text-3xl font-bold text-white mt-2" data-testid="total-price">
                £{(pricing.total_pence / 100).toFixed(2)}
              </p>
              <p className="text-[10px] text-[#E8E5DB]/60 mt-1">
                New allowance: {pricing.new_max_players} players × {pricing.new_max_rounds} rounds
              </p>
            </>
          ) : (
            <p className="text-sm text-[#E8E5DB]">Select the extra capacity you need</p>
          )}
        </Card>

        {/* Pay button */}
        <Button onClick={handleCheckout}
          disabled={!pricing || pricing.total_pence <= 0 || submitting || pricingLoading}
          className="w-full h-14 bg-[#E59000] hover:bg-[#CC8000] text-white rounded-xl text-base font-bold disabled:opacity-50"
          data-testid="pay-btn">
          {submitting ? (
            <Spinner className="w-5 h-5 animate-spin mr-2" />
          ) : (
            <CreditCard className="w-5 h-5 mr-2" weight="bold" />
          )}
          {submitting ? 'Creating checkout...' : 'Pay and Add'}
        </Button>
      </div>
    </div>
  );
}
